import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import logger from 'src/logger';
import { TResourcesRequest, TSiteResources } from 'src/types/resources.type';
import { miniSerializeErrorOverride } from './serialize-error';
import { fetchAuthResourcesService, fetchUnauthResourcesService } from 'src/services/resources/resources.service';
import { CustomError } from 'src/lib/error';
import { RootState } from '.';
import { getSessionIdentifier } from 'src/helpers/session.helpers';
import { filterIndustryResourcesByLocalTemplates, industryListMapper } from 'src/helpers/resources.helper';

export const fetchUnauthResourcesAction = createAsyncThunk(
  'resources/unauth/fetch',
  async (payload: TResourcesRequest) => {
    try {
      return await fetchUnauthResourcesService(payload);
    } catch (err) {
      logger.reportError(err, { isAPI: true });
      throw err;
    }
  },
  { serializeError: miniSerializeErrorOverride },
);

export const fetchAuthResourcesAction = createAsyncThunk(
  'resources/auth/fetch',
  async (payload: TResourcesRequest, { getState }) => {
    const { session } = getState() as RootState;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      return await fetchAuthResourcesService({ ...payload, ...getSessionIdentifier(session) });
    } catch (err) {
      logger.reportError(err, { isAPI: true });
      throw err;
    }
  },
  { serializeError: miniSerializeErrorOverride },
);

export const getResourcesAction = createAsyncThunk(
  'resources/fetch',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const resources = selectResources(state);

    // If resources already exist, return them
    if (resources) {
      return resources;
    }

    const { session } = state;
    const payload = { t: ['i', 't'] };

    try {
      let response: TSiteResources;
      if (session) {
        response = await dispatch(fetchAuthResourcesAction(payload)).unwrap();
      } else {
        response = await dispatch(fetchUnauthResourcesAction(payload)).unwrap();
      }

      return response.resources;
    } catch (err) {
      logger.reportError(err, { isAPI: true });
      throw err;
    }
  },
  { serializeError: miniSerializeErrorOverride },
);

export const getIndustryListAction = createAsyncThunk(
  'resources/industryList/fetch',
  async (_, { dispatch }) => {
    try {
      const resources: TSiteResources['resources'] = await dispatch(getResourcesAction()).unwrap();

      // Extract only industry related resources (type: 'i')
      const industryResourcesFromAPI = resources.filter(res => res.type === 'i');

      // Filter industry resources to include only locally available templates
      const industryResourcesFiltered = filterIndustryResourcesByLocalTemplates(industryResourcesFromAPI);

      return industryListMapper(industryResourcesFiltered);
    } catch (err) {
      logger.reportError(err, { isAPI: true });
      throw err;
    }
  },
  { serializeError: miniSerializeErrorOverride },
);

const resourcesSlice = createSlice({
  name: 'resources',
  initialState: [] as TSiteResources['resources'],
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getResourcesAction.fulfilled, (state, action) => {
      return action.payload;
    });
  },
});

export const selectResources = createSelector(
  (state: RootState) => state.resources,
  resources => (resources.length > 0 ? resources : null),
);

export default resourcesSlice.reducer;
